import {
  fromConsultationSnapshotList,
  fromConsultationStateSnapshot,
  fromConsultationsReviewAggregateList,
  fromConsultationsReviewSummary,
  fromDocumentAssignedSummary,
  fromDocumentsReviewAggregateList,
  fromDocumentsReviewSummary,
  fromProcessorSnapshotList,
  fromProcessorStateSnapshot,
  fromTasksSnapshotList,
  fromTasksStateSnapshot,
} from '@medprompt/shared';

/**
 * InsightsService - Handles insights and analytics API operations
 */
export class InsightsService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    console.log('InsightsService initialized');
  }

  /**
   * Fetch document processor insights for a date range
   * @param {Date} from - Start date
   * @param {Date} to - End date
   * @param {string} [level] - Optional snapshot level (HOURLY or DAILY)
   * @returns {Promise<Object>} Insights data
   */
  async fetchInsights(from, to, level) {
    const fromMillis = from.getTime();
    const toMillis = to.getTime();
    let url = `/api/insights/documents/fetch?from=${fromMillis}&to=${toMillis}`;
    if (level != null && level !== '') {
      url += `&level=${encodeURIComponent(level)}`;
    }
    return fromProcessorSnapshotList(await this.apiClient.get(url));
  }

  /**
   * Fetch task insights snapshots for a date range
   * @param {Date} from - Start date
   * @param {Date} to - End date
   * @param {string} [level] - Optional snapshot granularity (e.g. HOURLY, DAILY)
   * @returns {Promise<Array>} Task snapshot rows
   */
  async fetchTasksSnapshots(from, to, level) {
    const fromMillis = from.getTime();
    const toMillis = to.getTime();
    let url = `/api/insights/tasks/fetch?from=${fromMillis}&to=${toMillis}`;
    if (level != null && level !== '') {
      url += `&level=${encodeURIComponent(level)}`;
    }
    return fromTasksSnapshotList(await this.apiClient.get(url));
  }

  /**
   * Fetch consultation (scribe) insights snapshots for a date range
   * @param {Date} from
   * @param {Date} to
   * @param {string} [level]
   * @returns {Promise<Array>}
   */
  async fetchConsultationsSnapshots(from, to, level) {
    const fromMillis = from.getTime();
    const toMillis = to.getTime();
    let url = `/api/insights/consultations/fetch?from=${fromMillis}&to=${toMillis}`;
    if (level != null && level !== '') {
      url += `&level=${encodeURIComponent(level)}`;
    }
    return fromConsultationSnapshotList(await this.apiClient.get(url));
  }

  /**
   * Record that a document was opened
   * @param {string} documentId
   * @returns {Promise<Object>} Response data
   */
  async recordDocumentOpened(documentId) {
    return this.apiClient.post(`/api/metadata/document/${documentId}/opened`);
  }

  /**
   * Record that a consultation was opened
   * @param {string} consultationId
   * @returns {Promise<Object>} Response data
   */
  async recordConsultationOpened(consultationId) {
    return this.apiClient.post(`/api/metadata/consultation/${consultationId}/opened`);
  }

  /**
   * Record that a task was opened
   * @param {string} taskId
   * @returns {Promise<Object>} Response data
   */
  async recordTaskOpened(taskId) {
    return this.apiClient.post(`/api/metadata/task/${taskId}/opened`);
  }

  /**
   * Fetch audit log for a document
   * @param {string} documentId
   * @returns {Promise<Array>} Audit log entries
   */
  async fetchDocumentAudit(documentId, options = {}) {
    return this.apiClient.get(`/api/audit/document/${documentId}/fetch`, options);
  }

  /**
   * Fetch audit log for a consultation
   * @param {string} consultationId
   * @returns {Promise<Object>} Consultation audit data
   */
  async fetchConsultationAudit(consultationId, options = {}) {
    return this.apiClient.get(`/api/audit/consultation/${consultationId}/fetch`, options);
  }

  /**
   * Record that a document was viewed
   * @param {string} documentId
   * @returns {Promise<Object>} Response data
   */
  async recordDocumentViewed(documentId) {
    return this.apiClient.post(`/api/metadata/document/${documentId}/viewed`);
  }

  /**
   * Fetch all document viewers
   * @returns {Promise<Array>} Array of document viewers
   */
  async fetchAllDocumentViewers() {
    return this.apiClient.get(`/api/metadata/documents/viewers/fetch`);
  }

  /**
   * Fetch viewers for a specific document
   * @param {string} documentId
   * @returns {Promise<Array>} Array of viewers for the document
   */
  async fetchDocumentViewers(documentId) {
    return this.apiClient.get(`/api/metadata/document/${documentId}/viewers/fetch`);
  }

  /**
   * Record that a task was viewed
   * @param {string} taskId
   * @returns {Promise<Object>} Response data
   */
  async recordTaskViewed(taskId) {
    return this.apiClient.post(`/api/metadata/task/${taskId}/viewed`);
  }

  /**
   * Fetch all task viewers
   * @returns {Promise<Array>} Array of task viewers
   */
  async fetchAllTaskViewers() {
    return this.apiClient.get(`/api/metadata/task/viewers/fetch`);
  }

  /**
   * Fetch viewers for a specific task
   * @param {string} taskId
   * @returns {Promise<Array>} Array of viewers for the task
   */
  async fetchTaskViewers(taskId) {
    return this.apiClient.get(`/api/metadata/task/${taskId}/viewers/fetch`);
  }

  /**
   * Fetch documents assigned in a date range
   * @param {Date} from - Start date
   * @param {Date} to - End date
   * @returns {Promise<Object>} Object containing documentsAssigned array and missingCount
   */
  async fetchDocumentsAssigned(from, to) {
    const fromMillis = from.getTime();
    const toMillis = to.getTime();
    
    return fromDocumentAssignedSummary(await this.apiClient.get(`/api/insights/documents/assigned/fetch?from=${fromMillis}&to=${toMillis}`));
  }

  /**
   * Fetch current assignments for all active documents (live snapshot)
   * @param {Object} [requestConfig] - Optional config for the HTTP client (e.g. `{ headers: { 'X-Target-Clinic-Id': clinicId } }`)
   * @returns {Promise<Object>} Object containing documentsAssigned array and missingCount
   */
  async fetchCurrentDocumentsAssigned(requestConfig) {
    return fromDocumentAssignedSummary(await this.apiClient.get('/api/insights/documents/assigned/current/fetch', requestConfig));
  }

  /**
   * Fetch audit log for a task
   * @param {string} taskId
   * @returns {Promise<Array>} Audit log entries
   */
  async fetchTaskAudit(taskId) {
    return this.apiClient.get(`/api/audit/task/${taskId}/fetch`);
  }

  /**
   * Fetch document review insights for a date range
   * @param {Date} from - Start date
   * @param {Date} to - End date
   * @returns {Promise<Object>} Document review summary data
   */
  async fetchDocumentReviewInsights(from, to) {
    const fromMillis = from.getTime();
    const toMillis = to.getTime();
    
    return fromDocumentsReviewSummary(await this.apiClient.get(`/api/insights/documents/review/summary/fetch?from=${fromMillis}&to=${toMillis}`));
  }

  /**
   * Fetch current processor state (real-time snapshot)
   * @param {Object} [requestConfig] - Optional config for the HTTP client (e.g. `{ headers: { 'X-Target-Clinic-Id': clinicId } }`)
   * @returns {Promise<Object>} Current processor state snapshot
   */
  async fetchCurrentProcessorState(requestConfig) {
    return fromProcessorStateSnapshot(await this.apiClient.get('/api/insights/documents/state/fetch', requestConfig));
  }

  /**
   * Fetch current tasks state (real-time snapshot)
   * @param {Object} [requestConfig] - Optional config for the HTTP client (e.g. `{ headers: { 'X-Target-Clinic-Id': clinicId } }`)
   * @returns {Promise<Object>} Current tasks state snapshot
   */
  async fetchCurrentTasksState(requestConfig) {
    return fromTasksStateSnapshot(await this.apiClient.get('/api/insights/tasks/state/fetch', requestConfig));
  }

  /**
   * Fetch current consultations state (real-time snapshot)
   * @param {Object} [requestConfig] - Optional config for the HTTP client (e.g. `{ headers: { 'X-Target-Clinic-Id': clinicId } }`)
   * @returns {Promise<Object>} Current consultations state snapshot
   */
  async fetchCurrentConsultationsState(requestConfig) {
    return fromConsultationStateSnapshot(await this.apiClient.get('/api/insights/consultations/state/fetch', requestConfig));
  }

  /**
   * Fetch documents review aggregate for a date range
   * @param {Date} from - Start date
   * @param {Date} to - End date
   * @returns {Promise<Array>} Array of DocumentsReviewAggregate objects
   */
  async fetchDocumentsReviewAggregate(from, to) {
    const fromMillis = from.getTime();
    const toMillis = to.getTime();
    
    return fromDocumentsReviewAggregateList(await this.apiClient.get(`/api/insights/documents/review/aggregate/fetch?from=${fromMillis}&to=${toMillis}`));
  }

  /**
   * Fetch consultation review summary for a date range
   * @param {Date} from
   * @param {Date} to
   * @returns {Promise<Object>}
   */
  async fetchConsultationsReviewSummary(from, to) {
    const fromMillis = from.getTime();
    const toMillis = to.getTime();
    return fromConsultationsReviewSummary(await this.apiClient.get(`/api/insights/consultations/review/summary/fetch?from=${fromMillis}&to=${toMillis}`));
  }

  /**
   * Fetch consultation review aggregate for a date range
   * @param {Date} from
   * @param {Date} to
   * @returns {Promise<Array>}
   */
  async fetchConsultationsReviewAggregate(from, to) {
    const fromMillis = from.getTime();
    const toMillis = to.getTime();
    return fromConsultationsReviewAggregateList(await this.apiClient.get(`/api/insights/consultations/review/aggregate/fetch?from=${fromMillis}&to=${toMillis}`));
  }

  /**
   * Record that a referral was opened
   * @param {string} referralId
   * @returns {Promise<void>}
   */
  async recordReferralOpened(referralId) {
    return this.apiClient.post(`/api/metadata/referral/${referralId}/opened`);
  }

  /**
   * Fetch audit log for a referral
   * @param {string} referralId
   * @returns {Promise<Array>} Audit log entries
   */
  async fetchReferralAudit(referralId) {
    return this.apiClient.get(`/api/audit/referral/${referralId}/fetch`);
  }
}

